import React from 'react';
import { useAuth } from 'react-oidc-context';
import { useRoles } from '../hooks/useRoles';
import PleaseWait from './PleaseWait';
import { useNavigate } from 'react-router-dom';

interface GuardedElementProps {
  forceLogin?: boolean;
  permittedRoles?: string[];
  children: React.ReactNode;
  activeClaimPrefix?: string;
  fallback: React.ReactNode;
}

const GuardedElement: React.FC<GuardedElementProps> = ({
  forceLogin = false,
  permittedRoles = undefined,
  children,
  activeClaimPrefix,
  fallback
}: GuardedElementProps) => {
  const auth = useAuth();
  const hasRoles = useRoles();
  const [isStartingAuth, setIsStartingAuth] = React.useState(false);
  const navigate = useNavigate();
  const [isInactiveUser, setIsInactiveUser] = React.useState<string | undefined>(undefined);
  const [isInactiveOrg, setIsInactiveOrg] = React.useState<string | undefined>(undefined);

  React.useEffect(() => {
    if (!auth.isAuthenticated && forceLogin) {
      (async () => await auth.signinRedirect({ state: window.location.pathname }))();
      setIsStartingAuth(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated, forceLogin]);

  React.useEffect(() => {
    if (auth.isAuthenticated) {
      setIsStartingAuth(false);
      activeClaimPrefix && setIsInactiveOrg(auth.user?.profile[`${activeClaimPrefix}organisation_status`] as string);
      activeClaimPrefix && setIsInactiveUser(auth.user?.profile[`${activeClaimPrefix}status`] as string);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth.isAuthenticated]);

  React.useEffect(() => {
    if (auth.isAuthenticated && isInactiveOrg === 'inactive') {
      navigate('/inactive-organisation');
    }
    if (auth.isAuthenticated && isInactiveUser === 'inactive') {
      navigate('/inactive-user');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, isInactiveUser, isInactiveOrg]);

  if (auth.isLoading || isStartingAuth) return <PleaseWait message={'Getting Details...'} />;
  if (!permittedRoles || permittedRoles.some((role) => hasRoles(role))) return <>{children}</>;
  if (forceLogin) return <>{fallback}</>;

  return null;
};

export default GuardedElement;
